import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  ArrowRight, 
  CheckCircle2, 
  MessageCircle, 
  FileText, 
  Shield, 
  Zap, 
  Database, 
  X, 
  Menu,
  ScanLine,
  Play,
  Quote,
  Star
} from "lucide-react";
import { Heart } from "lucide-react";
import ExpoLeadHandheldMockup from "../components/ExpoLeadHandheldMockup";

const LandingPage = () => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [showScannerModal, setShowScannerModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false); // Video modal state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 80);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col min-h-screen font-sans text-gray-900 bg-white selection:bg-blue-200 overflow-x-hidden relative">
      
      {/* 1. RESPONSIVE HEADER */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b ${
          isScrolled || mobileMenuOpen
            ? "bg-white/90 backdrop-blur-md border-gray-200 py-3 shadow-sm" 
            : "bg-transparent border-transparent py-5"
        }`}
      >
        <div className="flex items-center justify-between w-full max-w-7xl px-4 sm:px-6 mx-auto">
          <h2 
            className="text-xl sm:text-2xl font-black text-gray-900 tracking-tighter cursor-pointer flex items-center gap-1.5" 
            onClick={() => {
              setMobileMenuOpen(false);
              navigate("/");
            }}
          >
            <span className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-zinc-950 text-white flex items-center justify-center">
              <ScanLine className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </span>
            Expo<span className="text-blue-600">Lead</span>
          </h2>

          <nav className="hidden md:flex items-center gap-8 font-semibold text-gray-600 text-sm">
            <button 
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} 
              className="hover:text-blue-600 transition"
            >
              Home
            </button>
            <Link 
              to="/features" 
              className="text-blue-600 font-bold hover:text-blue-700 transition flex items-center gap-1.5"
            >
              <span className="w-2 h-2 rounded-full bg-blue-600 animate-pulse"></span>
              Features
            </Link>
            <Link to="/about" className="hover:text-blue-600 transition">
              About Us
            </Link>
          </nav>

          <div className="hidden md:flex items-center gap-4">
            <button 
              onClick={() => navigate("/auth/login")} 
              className="text-sm font-bold text-gray-700 hover:text-blue-600 transition"
            >
              Log in
            </button>
            <button 
              onClick={() => navigate("/auth/register")} 
              className={`px-5 py-2.5 text-sm font-bold text-white bg-blue-600 rounded-full hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/30 ${
                isScrolled ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4 pointer-events-none"
              }`}
            >
              Get Started
            </button>
          </div>

          <div className="flex md:hidden items-center gap-2">
            <button 
              onClick={() => navigate("/auth/login")} 
              className="text-xs font-bold text-gray-800 hover:text-blue-600 px-2 py-1"
            >
              Log in
            </button>
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-slate-800 hover:text-blue-600 rounded-xl bg-slate-100 hover:bg-slate-200 transition-colors"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* MOBILE DROPDOWN */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white/95 backdrop-blur-xl border-b border-gray-200 px-6 py-4 mt-2 space-y-3 shadow-xl">
            <button 
              onClick={() => { setMobileMenuOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); }} 
              className="block w-full text-left py-2 font-bold text-slate-800 text-sm"
            >
              Home
            </button>
            <Link to="/features" onClick={() => setMobileMenuOpen(false)} className="block py-2 font-bold text-blue-600 text-sm">Features</Link>
            <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="block py-2 font-bold text-slate-800 text-sm">About Us</Link>
            <button 
              onClick={() => { setMobileMenuOpen(false); navigate("/auth/register"); }} 
              className="w-full py-3 bg-blue-600 text-white font-bold rounded-xl text-sm"
            >
              Get Started
            </button>
          </div>
        )}
      </header>

      {/* 2. HERO SECTION */}
      <main className="flex-grow pt-20">
        <section className="relative flex justify-center px-4 sm:px-6 py-8 sm:py-12 overflow-hidden lg:py-20">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#f0f0f0_1px,transparent_1px),linear-gradient(to_bottom,#f0f0f0_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] -z-20"></div>
          <div className="absolute top-20 left-1/2 -translate-x-1/2 w-full max-w-4xl h-[400px] bg-gradient-to-tr from-blue-400/20 to-indigo-500/20 blur-[100px] rounded-full -z-10"></div>

          <div className="flex flex-col items-center w-full max-w-7xl lg:flex-row lg:gap-8 z-10">
            <div className="flex flex-col items-center flex-1 text-center lg:items-start lg:text-left">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 mb-6 sm:mb-8 text-xs font-bold text-blue-700 border border-blue-200 bg-blue-50/80 backdrop-blur-sm rounded-full shadow-sm">
                <span className="relative flex w-2 h-2">
                  <span className="absolute inline-flex w-full h-full bg-blue-400 rounded-full opacity-75 animate-ping"></span>
                  <span className="relative inline-flex w-2 h-2 bg-blue-500 rounded-full"></span>
                </span>
                Next-Gen Lead Capture is Live
              </div>

              <h1 className="text-3xl sm:text-5xl lg:text-7xl font-extrabold text-gray-900 tracking-tight leading-[1.15] mb-4 sm:mb-6">
                Digitize exhibition leads <br className="hidden md:block"/>
                in <span className="inline-block text-transparent animate-bounce bg-clip-text bg-gradient-to-r from-black via-orange-600 to-amber-400">
  milliseconds.
</span>
              </h1>
              
              <p className="max-w-2xl mb-6 sm:mb-8 text-sm sm:text-lg leading-relaxed text-gray-600">
                The ultimate B2B companion for your next exhibition. Scan physical business cards, organize data instantly, and trigger automated WhatsApp follow-ups before leads even leave your booth.
              </p>

              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 sm:gap-4 mb-6 sm:mb-8 w-full sm:w-auto">
                <button
                  onClick={() => navigate("/auth/register")}
                  className="w-full sm:w-auto px-6 sm:px-8 py-3.5 sm:py-4 bg-blue-600 hover:bg-blue-700 text-white font-black text-sm sm:text-base rounded-2xl shadow-xl shadow-blue-600/30 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2"
                >
                  Start Scanning Free <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                <button
                  onClick={() => setShowScannerModal(true)}
                  className="w-full sm:w-auto px-6 sm:px-7 py-3.5 sm:py-4 bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-sm sm:text-base rounded-2xl transition-all"
                >
                  How It Works
                </button>
              </div>
              
              <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 p-4 sm:p-5 transition-all border border-gray-100 shadow-xl lg:justify-start bg-white/60 backdrop-blur-xl rounded-2xl shadow-blue-900/5 w-full sm:w-auto">
                <div className="flex flex-col items-center lg:items-start">
                  <span className="mb-0.5 text-[9px] sm:text-[10px] font-bold tracking-widest text-gray-400 uppercase">OCR Speed</span>
                  <span className="text-base sm:text-lg font-black text-gray-900">&lt; 200<span className="text-xs text-blue-600">ms</span></span>
                </div>
                <div className="w-px h-8 bg-gray-200"></div>
                <div className="flex flex-col items-center lg:items-start">
                  <span className="mb-0.5 text-[9px] sm:text-[10px] font-bold tracking-widest text-gray-400 uppercase">Data Sync</span>
                  <span className="text-base sm:text-lg font-black text-gray-900">Real-time</span>
                </div>
                <div className="w-px h-8 bg-gray-200"></div>
                <div className="flex flex-col items-center lg:items-start">
                  <span className="mb-0.5 text-[9px] sm:text-[10px] font-bold tracking-widest text-gray-400 uppercase">Security</span>
                  <span className="flex items-center gap-1 text-base sm:text-lg font-black text-emerald-600">
                    <Shield className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-600" />
                    AES-256
                  </span>
                </div>
              </div>
            </div>

            <div className="relative flex-1 w-full mt-8 lg:mt-0 z-10 flex justify-center">
              <ExpoLeadHandheldMockup />
            </div>
          </div>
        </section>

        {/* 3. WHATSAPP AUTOMATION SECTION */}
        <section className="py-16 sm:py-20 bg-white overflow-hidden border-t border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-8">
              <div className="w-full lg:w-1/2 max-w-2xl">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-bold border border-emerald-200 mb-4">
                  <MessageCircle className="w-3.5 h-3.5" /> Instant Engagement
                </div>
                <h2 className="text-2xl sm:text-4xl font-extrabold text-slate-900 mb-4 sm:mb-6 tracking-tight leading-tight">
                  Run AI-powered automations that <br className="hidden md:block"/> Click to WhatsApp
                </h2>
                <p className="text-sm sm:text-lg text-slate-600 mb-4 sm:mb-6 leading-relaxed">
                  Reach out to your trade show visitors instantly. 5X your lead generations & 2-3X conversions by connecting while they are still at your booth!
                </p>
                <button 
                  onClick={() => navigate("/features")}
                  className="bg-slate-950 hover:bg-slate-800 text-white font-bold py-3 px-6 sm:py-3.5 sm:px-8 rounded-xl transition-all shadow-xl flex items-center gap-2 text-sm sm:text-base hover:scale-[1.02] active:scale-95"
                >
                  Explore Features <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>

              <div className="w-full lg:w-1/2 relative">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 sm:w-96 h-80 sm:h-96 bg-emerald-400/20 rounded-full blur-[80px] -z-10"></div>
                <div className="relative bg-[#F4FAF6] p-4 sm:p-10 rounded-3xl sm:rounded-[2.5rem] border border-emerald-100 shadow-xl shadow-emerald-900/5">
                  <div className="bg-white p-4 sm:p-5 rounded-2xl shadow-xl border border-slate-100 max-w-[240px] sm:max-w-[260px] mb-6 sm:mb-8 relative z-10 transform -rotate-3 hover:rotate-0 transition-transform">
                    <div className="flex justify-between items-start mb-3 sm:mb-4">
                      <div className="flex items-center gap-2.5 sm:gap-3">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-black text-base sm:text-lg">TS</div>
                        <div>
                          <p className="font-bold text-xs sm:text-sm text-slate-900">Tony Stark</p>
                          <p className="text-[10px] sm:text-xs font-semibold text-slate-500">Director</p>
                        </div>
                      </div>
                      <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-500" />
                    </div>
                    <div className="w-full bg-[#1e6fba] text-white text-[11px] sm:text-xs font-bold py-2 sm:py-2.5 rounded-xl flex justify-center items-center gap-2 shadow-md">
                      <MessageCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Send Message
                    </div>
                  </div>

                  <div className="bg-[#EFEAE2] p-3.5 sm:p-4 rounded-3xl shadow-2xl border border-slate-200/60 max-w-[420px] ml-auto relative z-20 transform translate-x-1 sm:translate-x-4 sm:-translate-y-6 hover:-translate-y-8 transition-transform">
                    <div className="flex items-center gap-2 mb-3 bg-white/80 backdrop-blur-sm p-2 sm:p-2.5 rounded-2xl border border-white">
                      <div className="w-6 h-6 sm:w-7 sm:h-7 bg-emerald-500 rounded-full flex items-center justify-center shadow-sm">
                        <MessageCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white"/>
                      </div>
                      <p className="text-[11px] sm:text-xs font-bold text-slate-800 flex items-center gap-1">
                        ExpoLead Automations <span className="text-blue-500 font-bold">✓</span>
                      </p>
                    </div>
                    
                    <div className="bg-white p-3 sm:p-3.5 rounded-2xl rounded-tr-sm shadow-sm mb-2 max-w-[98%] ml-auto relative text-xs text-slate-800 space-y-1.5">
                      <p className="font-bold">Hi Tony Stark,</p>
                      <p className="text-slate-600 leading-relaxed text-[11px] sm:text-xs">
                        It was a pleasure meeting you at our booth today! We just scanned your business card and wanted to drop our digital profile directly into your chat.
                      </p>
                      <div className="p-2 bg-slate-50 rounded-xl border border-slate-100 text-[10px] sm:text-[11px] space-y-0.5">
                        <p className="font-bold text-slate-700">Contact Details:</p>
                        <p>🌐 <a href="https://expolead.thensgroup.in" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">expolead.thensgroup.in</a></p>
                        <p>📧 <a href="mailto:expolead@thensgroup.in" className="text-blue-600 hover:underline">expolead@thensgroup.in</a></p>
                      </div>
                      <div className="flex justify-between items-end pt-0.5">
                        <span className="text-[9px] text-slate-400">ExpoLead System</span>
                        <span className="text-[8px] sm:text-[9px] text-slate-400 font-medium">9:30 AM</span>
                      </div>
                    </div>
                    
                    <div className="bg-white p-2.5 sm:p-3 rounded-2xl rounded-tr-sm shadow-sm flex items-center gap-2.5 max-w-[98%] ml-auto">
                      <div className="bg-red-50 p-2 sm:p-2.5 rounded-xl text-red-500">
                        <FileText className="w-5 h-5 sm:w-6 sm:h-6" />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-800 line-clamp-1">ExpoLead_Catalog_2026.pdf</p>
                        <p className="text-[9px] sm:text-[10px] font-semibold text-slate-400">2.4 MB • Digital Brochure</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 4. FEATURE GRID */}
        <section className="relative py-16 sm:py-24 bg-white border-t border-slate-100">
          <div className="px-4 sm:px-6 mx-auto max-w-7xl">
            <h2 className="mb-12 sm:mb-16 text-2xl sm:text-4xl font-bold text-center text-gray-900">
              Everything you need on the exhibition floor
            </h2>
            
            <div className="grid gap-6 sm:gap-8 md:grid-cols-3">
              <div 
                onClick={() => setShowScannerModal(true)}
                className="relative p-6 sm:p-8 transition-all duration-300 bg-white border border-gray-200 shadow-sm cursor-pointer group rounded-3xl hover:border-blue-300 hover:shadow-2xl hover:shadow-blue-500/10 hover:-translate-y-2"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center justify-center text-white bg-blue-600 w-12 h-12 sm:w-14 sm:h-14 rounded-2xl shadow-lg shadow-blue-600/30">
                    <Zap className="w-6 h-6 sm:w-7 sm:h-7" />
                  </div>
                  <span className="px-3 py-1 text-xs font-bold text-blue-600 bg-blue-50 rounded-full group-hover:bg-blue-100 transition">
                    See Process &rarr;
                  </span>
                </div>
                <h3 className="mb-2 sm:mb-3 text-lg sm:text-xl font-bold text-gray-900">Smart Card Scanner</h3>
                <p className="leading-relaxed text-gray-600 text-xs sm:text-sm">
                  Extract names, emails, and phone numbers instantly using our cloud OCR engine. Click to see how it works.
                </p>
              </div>

              <div className="p-6 sm:p-8 transition duration-300 bg-white border border-gray-200 shadow-sm rounded-3xl hover:shadow-xl">
                <div className="flex items-center justify-center mb-6 text-white bg-slate-900 w-12 h-12 sm:w-14 sm:h-14 rounded-2xl">
                  <Database className="w-6 h-6 sm:w-7 sm:h-7" />
                </div>
                <h3 className="mb-2 sm:mb-3 text-lg sm:text-xl font-bold text-gray-900">Cloud Database</h3>
                <p className="leading-relaxed text-gray-600 text-xs sm:text-sm">
                  All contacts are safely synchronized to your secure database. Add context, tags, and follow-up reminders instantly.
                </p>
              </div>

              <div className="p-6 sm:p-8 transition duration-300 bg-white border border-gray-200 shadow-sm rounded-3xl hover:shadow-xl">
                <div className="flex items-center justify-center mb-6 text-white bg-emerald-500 w-12 h-12 sm:w-14 sm:h-14 rounded-2xl shadow-lg shadow-emerald-500/30">
                  <MessageCircle className="w-6 h-6 sm:w-7 sm:h-7" />
                </div>
                <h3 className="mb-2 sm:mb-3 text-lg sm:text-xl font-bold text-gray-900">WhatsApp Outreach</h3>
                <p className="leading-relaxed text-gray-600 text-xs sm:text-sm">
                  Trigger automated greetings and digital company brochures directly to your leads while they are still walking around.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 5. DOWNLOAD MOBILE SHOWCASE SECTION */}
        <section className="relative py-16 sm:py-24 bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-900 text-white overflow-hidden">
          <div className="px-4 sm:px-6 mx-auto max-w-7xl">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-16">
              
              <div className="flex-1 text-center lg:text-left z-10">
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold bg-white/10 text-blue-300 border border-white/10 mb-6 backdrop-blur-md">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                  Native App Available for Android & iOS
                </div>
                
                <h2 className="text-3xl sm:text-5xl font-black tracking-tight leading-tight mb-4 sm:mb-6">
                  Take the ExpoLead scanner <br className="hidden sm:inline" />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-300 to-emerald-400">in your pocket.</span>
                </h2>
                
                <p className="text-sm sm:text-lg text-slate-300 max-w-xl mx-auto lg:mx-0 mb-8 leading-relaxed">
                  Never lose a trade show lead again. Download the app today to unlock high-speed card scanning and instant WhatsApp outreach right on the exhibition floor.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg mx-auto lg:mx-0 mb-10 text-xs sm:text-sm text-slate-200 text-left">
                  <div className="flex items-center gap-2.5">
                    <div className="w-5 h-5 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center font-bold">✓</div>
                    <span>Instant Business Card OCR</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <div className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold">✓</div>
                    <span>WhatsApp Brochure Automation</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <div className="w-5 h-5 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center font-bold">✓</div>
                    <span>Real-Time Cloud Team Sync</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <div className="w-5 h-5 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center font-bold">✓</div>
                    <span>Secure AES-256 Encryption</span>
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4">
                  <a href="https://play.google.com/store/apps/details?id=in.thensgroup.expolead" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-6 py-3.5 bg-white text-gray-900 rounded-2xl font-bold shadow-xl hover:bg-blue-50 hover:scale-105 active:scale-95 transition-all">
                    <svg className="w-6 h-6 shrink-0" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M3.609 1.814L13.792 12 3.61 22.186a2.373 2.373 0 0 1-.61-1.586V3.4a2.373 2.373 0 0 1 .609-1.586z"/>
                      <path fill="#EA4335" d="M15.209 13.415l2.583 2.583-11.23 6.363 8.647-8.946z"/>
                      <path fill="#FBBC05" d="M17.792 10.832l3.29 1.865a1.2 1.2 0 0 1 0 2.106l-3.29 1.865-2.887-2.887 2.887-2.949z"/>
                      <path fill="#34A853" d="M15.209 10.585L6.562 2.303l11.23 6.363-2.583 2.583z"/>
                    </svg>
                    <div className="text-left">
                      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold -mb-0.5">GET IT ON</div>
                      <div className="text-sm font-black leading-none">Google Play</div>
                    </div>
                  </a>

                  <a href="https://apple.com/app-store" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-6 py-3.5 bg-white/10 text-white border border-white/20 rounded-2xl font-bold backdrop-blur-md hover:bg-white/20 hover:scale-105 active:scale-95 transition-all">
                    <svg className="w-6 h-6 shrink-0 fill-current" viewBox="0 0 24 24">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.32c.67-.82 1.13-1.96.99-3.12-.98.04-2.19.66-2.88 1.48-.62.73-1.16 1.89-.99 3.03 1.1.09 2.22-.55 2.88-1.39"/>
                    </svg>
                    <div className="text-left">
                      <div className="text-[10px] uppercase tracking-wider text-slate-300 font-semibold -mb-0.5">DOWNLOAD ON THE</div>
                      <div className="text-sm font-black leading-none">App Store</div>
                    </div>
                  </a>
                </div>

              </div>

              <div className="flex-1 relative flex justify-center items-center w-full max-w-md lg:max-w-none">
                <div className="absolute w-72 h-72 bg-gradient-to-tr from-blue-500 to-emerald-400 rounded-full blur-2xl opacity-30"></div>
                
                <div className="relative z-10 w-52 sm:w-64 bg-slate-900 border-4 border-slate-700/80 rounded-[2.5rem] p-4 shadow-2xl transform -rotate-6 translate-x-4 sm:translate-x-6 hover:rotate-0 transition-transform duration-500">
                  <div className="w-12 h-1.5 bg-slate-700 rounded-full mx-auto mb-4"></div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-slate-800/80 rounded-2xl border border-slate-700/50">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center font-bold text-xs">TS</div>
                        <div>
                          <div className="text-xs font-bold text-white">Tony Stark</div>
                          <div className="text-[10px] text-slate-400">Stark Industries</div>
                        </div>
                      </div>
                      <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                    </div>
                    <div className="p-3 bg-slate-800/80 rounded-2xl border border-slate-700/50 space-y-2">
                      <div className="h-2 w-3/4 bg-slate-700 rounded animate-pulse"></div>
                      <div className="h-2 w-1/2 bg-slate-700 rounded animate-pulse"></div>
                      <div className="mt-3 py-1.5 px-3 bg-blue-600/30 text-blue-300 rounded-xl text-[10px] font-bold text-center border border-blue-500/30">
                        ⚡ OCR Extracted in 180ms
                      </div>
                    </div>
                  </div>
                </div>

                <div className="relative z-20 w-52 sm:w-64 bg-white border-4 border-slate-200 rounded-[2.5rem] p-4 shadow-2xl text-gray-900 transform rotate-6 -translate-x-4 sm:-translate-x-6 hover:rotate-0 transition-transform duration-500">
                  <div className="w-12 h-1.5 bg-slate-200 rounded-full mx-auto mb-4"></div>
                  <div className="space-y-3">
                    <div className="p-3 bg-blue-50 rounded-2xl border border-blue-100 flex items-center justify-between">
                      <div>
                        <div className="text-xs font-bold text-blue-900">WhatsApp Sent!</div>
                        <div className="text-[10px] text-blue-600">Company Brochure PDF</div>
                      </div>
                      <div className="w-7 h-7 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold text-xs">✓</div>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100 text-center">
                      <div className="text-[11px] font-bold text-gray-700 mb-1">New Trade Show Lead</div>
                      <div className="text-2xl font-black text-gray-900">+48</div>
                      <div className="text-[9px] text-emerald-600 font-bold uppercase tracking-wider">SYNCED TO CLOUD TODAY</div>
                    </div>
                  </div>
                </div>

              </div>

            </div>
          </div>
        </section>

      </main>

      {/* 6. MODALS */}
      {/* SCANNER PROCESS MODAL */}
      {showScannerModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
          <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setShowScannerModal(false)}></div>
          <div className="relative w-full max-w-4xl overflow-hidden bg-white shadow-2xl rounded-[2.5rem]">
            <div className="flex items-center justify-between p-6 sm:p-8 border-b border-gray-100 bg-gray-50/50">
              <h3 className="text-xl sm:text-2xl font-black text-gray-900">How the AI Scanner Works</h3>
              <button onClick={() => setShowScannerModal(false)} className="p-2 text-gray-400 hover:text-gray-800 rounded-full hover:bg-gray-200">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="grid gap-6 p-6 sm:p-10 text-center bg-white md:grid-cols-3">
              <div className="flex flex-col items-center">
                <div className="relative flex items-center justify-center w-full h-36 sm:h-44 mb-4 sm:mb-6 border-2 border-dashed border-gray-300 bg-gray-50/80 rounded-2xl">
                  <span className="font-semibold text-gray-400 text-xs sm:text-sm">Drop Image 1 Here</span>
                </div>
                <div className="flex items-center justify-center w-8 h-8 mb-3 font-bold text-white bg-slate-900 rounded-full text-sm">1</div>
                <h4 className="text-base sm:text-lg font-bold text-gray-900">Snap the Card</h4>
                <p className="mt-1 text-xs text-gray-500 leading-relaxed max-w-xs">Open the ExpoLead scanner and capture any physical business card.</p>
              </div>

              <div className="flex flex-col items-center">
                <div className="relative flex items-center justify-center w-full h-36 sm:h-44 mb-4 sm:mb-6 border border-blue-200 bg-blue-50/50 rounded-2xl overflow-hidden shadow-sm">
                  <span className="font-semibold text-blue-500 text-xs sm:text-sm z-10">Drop Image 2 Here</span>
                  <div className="absolute left-0 right-0 h-1 bg-blue-600 shadow-[0_0_15px_#2563eb] animate-[laserScan_2.5s_ease-in-out_infinite]" />
                  <div className="absolute left-0 right-0 h-10 bg-gradient-to-b from-blue-500/20 to-transparent animate-[laserScan_2.5s_ease-in-out_infinite]" />
                </div>
                <div className="flex items-center justify-center w-8 h-8 mb-3 font-bold text-white bg-blue-600 rounded-full text-sm">2</div>
                <h4 className="text-base sm:text-lg font-bold text-gray-900">Data Extraction</h4>
                <p className="mt-1 text-xs text-gray-500 leading-relaxed max-w-xs">Our advanced OCR technology instantly reads the image and categorizes contact fields.</p>
              </div>

              <div className="flex flex-col items-center">
                <div className="relative flex items-center justify-center w-full h-36 sm:h-44 mb-4 sm:mb-6 border border-emerald-200 bg-emerald-50/50 rounded-2xl">
                  <span className="font-semibold text-emerald-600 text-xs sm:text-sm">Drop Image 3 Here</span>
                </div>
                <div className="flex items-center justify-center w-8 h-8 mb-3 font-bold text-white bg-emerald-500 rounded-full text-sm">3</div>
                <h4 className="text-base sm:text-lg font-bold text-gray-900">Sync & Connect</h4>
                <p className="mt-1 text-xs text-gray-500 leading-relaxed max-w-xs">The contact is saved and an automated WhatsApp intro is queued immediately.</p>
              </div>
            </div>
            
            <div className="flex justify-end p-6 border-t border-gray-100 bg-gray-50/80">
              <button onClick={() => { setShowScannerModal(false); navigate("/auth/register"); }} className="px-6 py-2.5 font-bold text-white bg-slate-900 hover:bg-slate-800 rounded-xl text-sm">Try it now</button>
            </div>
          </div>
        </div>
      )}

      {/* VIDEO CASE STUDY MODAL */}
      {showVideoModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
          <div className="absolute inset-0 bg-gray-900/80 backdrop-blur-md" onClick={() => setShowVideoModal(false)}></div>
          <div className="relative w-full max-w-3xl overflow-hidden bg-slate-950 shadow-2xl rounded-3xl border border-slate-800 text-white">
            <div className="flex items-center justify-between p-4 border-b border-slate-800">
              <h3 className="text-base font-bold text-white">Apex Industrial Case Study</h3>
              <button onClick={() => setShowVideoModal(false)} className="p-1.5 text-slate-400 hover:text-white rounded-full bg-slate-800">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="aspect-video w-full flex items-center justify-center bg-slate-900 relative">
              <div className="text-center p-6 space-y-3">
                <Play className="w-16 h-16 text-blue-500 mx-auto fill-blue-500 animate-pulse" />
                <p className="text-lg font-bold text-slate-200">Booth Demo Preview</p>
                <p className="text-xs text-slate-400 max-w-md mx-auto">
                  Watch how Rajesh's sales team captured 380+ cards and sent WhatsApp brochures live on the exhibition floor.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 7. FOOTER */}
<footer className="py-8 bg-white border-t border-gray-200">
  <div className="flex flex-col items-center justify-between px-6 mx-auto max-w-7xl md:flex-row gap-6">
    
    {/* Left Side: Copyright */}
    <p className="text-xs sm:text-sm font-medium text-gray-500 text-center md:text-left">
      Copyright &copy; {new Date().getFullYear()} NS Engineers. All rights reserved.
    </p>

    {/* Center Side: Made in India & Meta Business Partner Badge */}
    <div className="flex flex-col sm:flex-row items-center gap-3">
    

      <span className="hidden sm:inline text-gray-300">•</span>

      {/* Meta Business Partner Badge Image */}
      <img 
        src="/meta-business-partner.png" 
        alt="Meta Business Partner" 
        className="h-8 sm:h-10 object-contain" 
      />
    </div>

    {/* Right Side: Links */}
    <div className="flex flex-wrap justify-center gap-4 sm:gap-6 text-xs sm:text-sm font-semibold text-gray-500">
      <Link to="/about" className="transition hover:text-blue-600">About Us</Link>
      <Link to="/help" className="transition hover:text-blue-600">Help</Link>
      <Link to="/privacy-policy" className="transition hover:text-blue-600">Privacy Policy</Link>
      <Link to="/terms-and-conditions" className="transition hover:text-blue-600">Terms & Conditions</Link>
      <Link to="/refund-policy" className="transition hover:text-blue-600">Refund Policy</Link>
    </div>

  </div>
</footer>
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes laserScan {
          0%, 100% { top: 0%; }
          50% { top: 95%; }
        }
      `}} />

    </div>
  );
};

export default LandingPage;